import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { Badge } from './badge';
import { cn, focusRing } from './utils';

export interface TabDefinition {
  readonly value: string;
  readonly label: string;
  /** Contador exibido ao lado do rótulo (ex.: nº de versões). */
  readonly count?: number;
  readonly disabled?: boolean;
  /** Marca a aba como contendo erro de validação. */
  readonly hasError?: boolean;
  readonly content: ReactNode;
}

export interface TabsProps {
  readonly tabs: readonly TabDefinition[];
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly className?: string;
  /** Nome do conjunto de abas para tecnologia assistiva. */
  readonly ariaLabel: string;
}

/**
 * Abas.
 *
 * Navegação por teclado do Radix (padrão APG): setas ←/→ movem entre abas,
 * Home/End vão para a primeira/última, e Tab sai da lista direto para o painel.
 * Usamos `activationMode="manual"`: com ativação automática, quem navega por
 * seta dispararia o carregamento de cada aba ao passar — na tela de detalhe do
 * documento isso significaria seis requisições só para chegar na última aba.
 *
 * `hasError` acrescenta ícone + texto oculto além da cor, porque no formulário
 * de documento a aba com erro precisa ser identificável sem depender de enxergar
 * o vermelho (WCAG 1.4.1).
 */
export function Tabs({ tabs, value, onValueChange, className, ariaLabel }: TabsProps) {
  return (
    <TabsPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      activationMode="manual"
      className={cn('flex min-w-0 flex-col', className)}
    >
      <TabsPrimitive.List
        aria-label={ariaLabel}
        className="flex min-w-0 gap-1 overflow-x-auto border-b border-border"
      >
        {tabs.map((tab) => (
          <TabsPrimitive.Trigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
            className={cn(
              'relative -mb-px flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 border-transparent px-3.5 py-2.5 text-sm font-medium',
              'text-content-muted transition-colors hover:text-content',
              'data-[state=active]:border-brand data-[state=active]:text-brand',
              'disabled:cursor-not-allowed disabled:opacity-55',
              focusRing,
            )}
          >
            {tab.hasError ? (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  aria-hidden="true"
                  focusable="false"
                  className="text-danger-content"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7.5v5.5" />
                  <path d="M12 16.5h.01" />
                </svg>
                <span className="qc-sr-only">Contém erro:</span>
              </>
            ) : null}
            <span>{tab.label}</span>
            {typeof tab.count === 'number' ? (
              <Badge tone="neutral" size="sm">
                {tab.count}
              </Badge>
            ) : null}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>

      {tabs.map((tab) => (
        <TabsPrimitive.Content
          key={tab.value}
          value={tab.value}
          className={cn('min-w-0 pt-5', focusRing)}
        >
          {tab.content}
        </TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  );
}
